import {
  authenticationSchema,
  AuthenticationType
} from "@repo/core/services/entities/Authentication";
import { apiInstance } from "@repo/core/services/instances/api";
import {
  loginWayupApi,
  wayupApiInstance
} from "@repo/core/services/instances/wayup-api";
import { clearWayupApiSession } from "@repo/core/stores/wayupApiAuth";
import { clearUnicoSession } from "@repo/core/stores/unico";
import { ApiErrorType } from "@repo/core/services/entities/Error";
import { TokenDataType } from "@repo/core/services/entities/User";
import { AuthResource } from "@repo/core/services/resources/auth";
import useAuthStore from "@repo/core/stores/auth";
import { AxiosError } from "axios";
import { useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { useAppVersion } from "@repo/core/hooks/useAppVersion";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { RequisitionsResource } from "@repo/core/services/resources/requisitions";
import { useStoresAndShopkeepersStore } from "@repo/core/stores/storesAndShopkeepers";
import { normalizeUnicoCaptureMaxAttempts } from "@repo/core/utils/unicoCaptureAttempts";
import { resumeSession } from "@repo/core/services/session/sessionEpoch";
import { isUnauthorizedResponse } from "@repo/core/services/session/sessionExpiration";

function isUnauthorizedLoginDependencyError(error: unknown): boolean {
  const axiosError = error as AxiosError;
  return isUnauthorizedResponse(
    axiosError.response?.status,
    axiosError.response?.data
  );
}

function getLoginErrorMessage(error: unknown): string {
  const axiosErr = error as AxiosError<
    ApiErrorType & { detail?: string; message?: string }
  >;
  const data = axiosErr.response?.data;
  if (data && typeof data === "object") {
    if (
      "error_description" in data &&
      typeof data.error_description === "string"
    ) {
      return data.error_description;
    }
    if ("detail" in data && typeof data.detail === "string") {
      return data.detail;
    }
    if ("message" in data && typeof data.message === "string") {
      return data.message;
    }
  }
  return "Erro ao realizar login. Por favor, tente novamente mais tarde.";
}

export const useLoginController = () => {
  // State
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { setAvailableShopkeepers, setAvailableStores, resetSessionState } =
    useStoresAndShopkeepersStore();
  const appVersion = useAppVersion();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<AuthenticationType>({
    resolver: zodResolver(authenticationSchema)
  });

  // Store
  const login = useAuthStore((state) => state.login);

  // Services
  const auth = new AuthResource(apiInstance);
  const proposals = new ProposalsResource(apiInstance);
  const requisitions = new RequisitionsResource(wayupApiInstance);

  const getStoresAndShopkeepersInfo = async (tokenData: string) => {
    const { data: shopkeepers } = await proposals.fetchShopkeepers(tokenData);
    const { data: stores } = await proposals.fetchStores(
      tokenData,
      shopkeepers[0].lojista
    );

    setAvailableShopkeepers(shopkeepers);
    setAvailableStores(stores);
  };

  // Handlers
  const onSubmit = handleSubmit(async (data) => {
    const { username, password } = data;
    try {
      resumeSession();
      setIsLoggingIn(true);
      setErrorMessage("");
      const [legacyAuth] = await Promise.all([
        auth.authentication({ username, password }),
        loginWayupApi({ usuario: username, senha: password })
      ]);

      const { data } = legacyAuth;

      const tokenData: TokenDataType = jwtDecode(data.access_token);

      try {
        await getStoresAndShopkeepersInfo(data.access_token);
      } catch (error) {
        if (isUnauthorizedLoginDependencyError(error)) {
          throw error;
        }

        console.error("Error to get stores and shopkeepers info", error);
      }

      const [permissionsResponse, unicoCaptureMaxAttemptsResponse] =
        await Promise.all([
          requisitions.getUserPermissions(),
          requisitions.getUnicoCaptureMaxAttempts().catch((error) => {
            if (isUnauthorizedLoginDependencyError(error)) {
              throw error;
            }

            console.error("Error to get Unico capture max attempts", error);
            return null;
          })
        ]);

      const unicoCaptureMaxAttempts = normalizeUnicoCaptureMaxAttempts(
        unicoCaptureMaxAttemptsResponse?.data?.qtdevezesviaunico
      );

      login({
        name: data?.strNome,
        username,
        token: data.access_token,
        tokenData,
        permissions: permissionsResponse.data,
        unicoCaptureMaxAttempts
      });
    } catch (error: unknown) {
      clearWayupApiSession();
      clearUnicoSession();
      resetSessionState();
      setIsLoggingIn(false);
      console.error(error);
      setErrorMessage(getLoginErrorMessage(error));
    }
  });

  // Exports
  return {
    isAuthenticated,
    isLoggingIn,
    errorMessage,
    control,
    onSubmit,
    errors,
    appVersion
  };
};
